import starVertex from './shaders/star-vertex.glsl?raw';
import starFragment from './shaders/star-fragment.glsl?raw';
import meteorVertex from './shaders/meteor-vertex.glsl?raw';
import meteorFragment from './shaders/meteor-fragment.glsl?raw';

export type StarShape
  = | 'circle'
    | 'square'
    | 'triangle'
    | 'star'
    | 'diamond'
    | 'pentagon'
    | 'hexagon'
    | 'heart'
    | 'cross'
    | 'moon'
    | 'cog'
    | 'flower'
    | 'spiral'
    | 'crescent'
    | 'arrow'
    | 'custom';

export interface StarfieldConfig {
  starCount: number;
  minStarSize: number;
  maxStarSize: number;
  starSpeed: number;
  meteorProbability: number;
  meteorSpeed: number;
  meteorLength: number;
  parallaxIntensity: number;
  starOpacity: number;
  twinkleSpeed: number;
  backgroundColor: string;
  starColors: string[];
  meteorColors: string[];
  meteorFadeStart: number;
  starShapes: StarShape[];
  customStarTexture?: string;
}

export interface Star {
  x: number;
  y: number;
  z: number;
  size: number;
  opacity: number;
  twinkle: number;
  twinkleSpeed: number;
  color: string;
  shape: number;
}

export interface Meteor {
  x: number;
  y: number;
  angle: number;
  speed: number;
  length: number;
  opacity: number;
  life: number;
  maxLife: number;
}

export class StarfieldRenderer {
  private canvas: HTMLCanvasElement;
  private gl: WebGL2RenderingContext;
  private config: StarfieldConfig;
  private stars: Star[] = [];
  private meteors: Meteor[] = [];

  private mouseX: number = 0;
  private mouseY: number = 0;
  private targetMouseX: number = 0;
  private targetMouseY: number = 0;
  private time: number = 0;
  private animationId: number | null = null;
  private eventElement: HTMLElement | null = null;

  private starProgram: WebGLProgram | null = null;
  private meteorProgram: WebGLProgram | null = null;

  private buffers = {
    star: {
      position: null as WebGLBuffer | null,
      size: null as WebGLBuffer | null,
      opacity: null as WebGLBuffer | null,
      color: null as WebGLBuffer | null,
      shape: null as WebGLBuffer | null,
    },
    meteor: {
      position: null as WebGLBuffer | null,
      progress: null as WebGLBuffer | null,
    },
  };

  private arrays = {
    star: {
      positions: new Float32Array(0),
      sizes: new Float32Array(0),
      opacities: new Float32Array(0),
      colors: new Float32Array(0),
      shapes: new Int32Array(0),
    },
    meteor: {
      positions: new Float32Array(0),
      progress: new Float32Array(0),
    },
  };

  private locations = {
    uniforms: {
      star: {
        resolution: null as WebGLUniformLocation | null,
        mouse: null as WebGLUniformLocation | null,
        parallaxIntensity: null as WebGLUniformLocation | null,
        customTexture: null as WebGLUniformLocation | null,
        hasCustomTexture: null as WebGLUniformLocation | null,
      },
      meteor: {
        resolution: null as WebGLUniformLocation | null,
        color: null as WebGLUniformLocation | null,
        opacity: null as WebGLUniformLocation | null,
      },
    },
    attributes: {
      star: {
        position: -1,
        size: -1,
        opacity: -1,
        color: -1,
        shape: -1,
      },
      meteor: {
        position: -1,
        progress: -1,
      },
    },
  };

  private colorCache: Map<string, { r: number; g: number; b: number; a: number }> = new Map();
  private customTexture: WebGLTexture | null = null;

  private readonly CONSTANTS = {
    PI_2: Math.PI * 2,
    PI_4: Math.PI / 4,
    PI_3_4: 3 * Math.PI / 4,
    DELTA_TIME: 0.016,
    METEOR_SEGMENTS: 20,
  };

  /**
   * 初始化 WebGL 星空渲染器
   * @param canvas HTMLCanvasElement - 用于渲染的画布元素
   * @param config Partial<StarfieldConfig> - 可选的配置参数
   */
  constructor(canvas: HTMLCanvasElement, config: Partial<StarfieldConfig> = {}) {
    this.canvas = canvas;
    this.gl = canvas.getContext('webgl2')!;

    if (!this.gl) {
      throw new Error('WebGL 2.0 not supported');
    }

    this.config = {
      starCount: 200,
      minStarSize: 1,
      maxStarSize: 3,
      starSpeed: 0.1,
      meteorProbability: 0.03,
      meteorSpeed: 2,
      meteorLength: 100,
      parallaxIntensity: 0.5,
      starOpacity: 0.8,
      twinkleSpeed: 0.02,
      backgroundColor: 'transparent',
      starColors: ['#1281FF'],
      meteorColors: ['#0D47A1'],
      meteorFadeStart: 0.3,
      starShapes: ['circle'],
      ...config,
    };

    // 初始化数据数组
    this.arrays.star.positions = new Float32Array(this.config.starCount * 2);
    this.arrays.star.sizes = new Float32Array(this.config.starCount);
    this.arrays.star.opacities = new Float32Array(this.config.starCount);
    this.arrays.star.colors = new Float32Array(this.config.starCount * 3);
    this.arrays.star.shapes = new Int32Array(this.config.starCount);
    this.arrays.meteor.positions = new Float32Array(this.CONSTANTS.METEOR_SEGMENTS * 2);
    this.arrays.meteor.progress = new Float32Array(this.CONSTANTS.METEOR_SEGMENTS);

    this.preCacheColors();
    this.loadCustomTexture();

    this.init();
  }

  private init(): void {
    this.setupCanvas();
    this.createShaders();
    this.createBuffers();
    this.cacheWebGLLocations();
    this.generateStars();
    this.setupEventListeners();
    this.start();
  }

  private setupCanvas(): void {
    const resizeCanvas = () => {
      this.canvas.width = this.canvas.clientWidth;
      this.canvas.height = this.canvas.clientHeight;
      this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    };

    resizeCanvas();

    window.addEventListener('resize', resizeCanvas);
  }

  private createShaders(): void {
    // 星星顶点着色器
    const starVertexShader = this.createShader(this.gl.VERTEX_SHADER, starVertex);

    // 星星片段着色器
    const starFragmentShader = this.createShader(this.gl.FRAGMENT_SHADER, starFragment);

    this.starProgram = this.createProgram(starVertexShader, starFragmentShader);

    // 流星顶点着色器
    const meteorVertexShader = this.createShader(this.gl.VERTEX_SHADER, meteorVertex);

    // 流星片段着色器
    const meteorFragmentShader = this.createShader(this.gl.FRAGMENT_SHADER, meteorFragment);

    this.meteorProgram = this.createProgram(meteorVertexShader, meteorFragmentShader);
  }

  private createShader(type: number, source: string): WebGLShader {
    const shader = this.gl.createShader(type);
    if (!shader) {
      throw new Error('Failed to create shader');
    }

    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);

    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      const error = this.gl.getShaderInfoLog(shader);
      this.gl.deleteShader(shader);
      throw new Error('Shader compilation error: ' + error);
    }

    return shader;
  }

  private createProgram(vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram {
    const program = this.gl.createProgram();
    if (!program) {
      throw new Error('Failed to create program');
    }

    this.gl.attachShader(program, vertexShader);
    this.gl.attachShader(program, fragmentShader);
    this.gl.linkProgram(program);

    if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
      const error = this.gl.getProgramInfoLog(program);
      this.gl.deleteProgram(program);
      throw new Error('Program linking error: ' + error);
    }

    return program;
  }

  private createBuffers(): void {
    this.buffers.star.position = this.gl.createBuffer();
    this.buffers.star.size = this.gl.createBuffer();
    this.buffers.star.opacity = this.gl.createBuffer();
    this.buffers.star.color = this.gl.createBuffer();
    this.buffers.star.shape = this.gl.createBuffer();
    this.buffers.meteor.position = this.gl.createBuffer();
    this.buffers.meteor.progress = this.gl.createBuffer();
  }

  private preCacheColors(): void {
    this.config.starColors.forEach(color => {
      this.getCachedColor(color);
    });
    this.config.meteorColors.forEach(color => {
      this.getCachedColor(color);
    });
    if (this.config.backgroundColor !== 'transparent') {
      this.getCachedColor(this.config.backgroundColor);
    }
  }

  private isPowerOf2(value: number) {
    return (value & (value - 1)) === 0;
  }

  private nextHighestPowerOfTwo(x: number) {
    --x;
    for (let i = 1; i < 32; i <<= 1) {
      x = x | x >> i;
    }
    return (x + 1);
  }

  private loadCustomTexture(): void {
    if (!this.config.customStarTexture) {
      return;
    }

    const img = new Image();

    img.onload = () => {
      this.customTexture = this.gl.createTexture();
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.customTexture);
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, img);
      this.gl.generateMipmap(this.gl.TEXTURE_2D);

      const ext = this.gl.getExtension('EXT_texture_filter_anisotropic');

      if (ext) {
        const maxAnisotropy = this.gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
        this.gl.texParameterf(this.gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, maxAnisotropy);
      }

      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR_MIPMAP_LINEAR);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);

      this.gl.bindTexture(this.gl.TEXTURE_2D, null);
    };

    img.onerror = () => {
      console.warn('Failed to load custom star texture');
      this.customTexture = null;
    };

    if (this.config.customStarTexture.startsWith('data:image/')) {
      img.src = this.config.customStarTexture;
    } else {
      console.warn('Custom star texture must be a base64 data URL');
    }
  }

  private cacheWebGLLocations(): void {
    if (this.starProgram) {
      this.locations.uniforms.star.resolution = this.gl.getUniformLocation(this.starProgram, 'u_resolution');
      this.locations.uniforms.star.mouse = this.gl.getUniformLocation(this.starProgram, 'u_mouse');
      this.locations.uniforms.star.parallaxIntensity = this.gl.getUniformLocation(this.starProgram, 'u_parallaxIntensity');
      this.locations.uniforms.star.customTexture = this.gl.getUniformLocation(this.starProgram, 'u_customTexture');
      this.locations.uniforms.star.hasCustomTexture = this.gl.getUniformLocation(this.starProgram, 'u_hasCustomTexture');

      this.locations.attributes.star.position = this.gl.getAttribLocation(this.starProgram, 'a_position');
      this.locations.attributes.star.size = this.gl.getAttribLocation(this.starProgram, 'a_size');
      this.locations.attributes.star.opacity = this.gl.getAttribLocation(this.starProgram, 'a_opacity');
      this.locations.attributes.star.color = this.gl.getAttribLocation(this.starProgram, 'a_color');
      this.locations.attributes.star.shape = this.gl.getAttribLocation(this.starProgram, 'a_shape');
    }

    if (this.meteorProgram) {
      this.locations.uniforms.meteor.resolution = this.gl.getUniformLocation(this.meteorProgram, 'u_resolution');
      this.locations.uniforms.meteor.color = this.gl.getUniformLocation(this.meteorProgram, 'u_color');
      this.locations.uniforms.meteor.opacity = this.gl.getUniformLocation(this.meteorProgram, 'u_opacity');

      this.locations.attributes.meteor.position = this.gl.getAttribLocation(this.meteorProgram, 'a_position');
      this.locations.attributes.meteor.progress = this.gl.getAttribLocation(this.meteorProgram, 'a_progress');
    }
  }

  private generateStars(): void {
    this.stars = [];
    const starColorsLength = this.config.starColors.length;
    const starShapesLength = this.config.starShapes.length;

    for (let i = 0; i < this.config.starCount; i++) {
      const randomShapeIndex = Math.floor(Math.random() * starShapesLength);
      const shapeName = this.config.starShapes[randomShapeIndex];
      const shapeType = this.getShapeType(shapeName);

      this.stars.push({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        z: Math.random() * 1000,
        size: this.config.minStarSize + Math.random() * (this.config.maxStarSize - this.config.minStarSize),
        opacity: Math.random() * this.config.starOpacity,
        twinkle: Math.random() * this.CONSTANTS.PI_2,
        twinkleSpeed: (Math.random() - 0.5) * this.config.twinkleSpeed,
        color: this.config.starColors[Math.floor(Math.random() * starColorsLength)],
        shape: shapeType,
      });
    }
  }

  private getShapeType(shapeName: string): number {
    switch (shapeName.toLowerCase()) {
      case 'circle': return 0;
      case 'square': return 1;
      case 'triangle': return 2;
      case 'star': return 3;
      case 'diamond': return 4;
      case 'pentagon': return 5;
      case 'hexagon': return 6;
      case 'heart': return 7;
      case 'cross': return 8;
      case 'moon': return 9;
      case 'cog': return 10;
      case 'flower': return 11;
      case 'spiral': return 12;
      case 'crescent': return 13;
      case 'arrow': return 14;
      case 'custom': return 9999;
      default: return 0;
    }
  }

  private setupEventListeners(): void {
    const element = this.eventElement || this.canvas;

    element.addEventListener('mousemove', this.mouseMoveHandler);
    element.addEventListener('mouseleave', this.mouseLeaveHandler);
  }

  private updateStars(): void {
    const starSpeed = this.config.starSpeed;
    const starOpacity = this.config.starOpacity;
    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;

    this.stars.forEach(star => {
      // 使用正弦和余弦函数创建平滑的漂移效果
      star.x += Math.sin(star.twinkle * 0.5) * starSpeed * 0.5;
      star.y += Math.cos(star.twinkle * 0.3) * starSpeed * 0.3;

      // 边界检查：当星星移出画布时，从对面重新进入
      if (star.x < (-5 - star.size)) {
        star.x = canvasWidth + star.size + 5;
      }
      if (star.x > canvasWidth + star.size + 5) {
        star.x = -5 - star.size;
      }
      if (star.y < (-5 - star.size)) {
        star.y = canvasHeight + star.size + 5;
      }
      if (star.y > canvasHeight + star.size + 5) {
        star.y = -5 - star.size;
      }

      // 更新闪烁效果
      star.twinkle += star.twinkleSpeed;
      star.opacity = (Math.sin(star.twinkle) + 1) * 0.5 * starOpacity;
    });
  }

  private updateMeteors(): void {
    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;

    // 随机生成新流星
    if (Math.random() < this.config.meteorProbability) {
      const startX = Math.random() * canvasWidth;
      const startY = -20;
      // 流星角度在 45° 到 135° 之间（左上到右下）
      const angle = this.CONSTANTS.PI_4 + Math.random() * (this.CONSTANTS.PI_3_4 - this.CONSTANTS.PI_4);

      this.meteors.push({
        x: startX,
        y: startY,
        angle: angle,
        speed: this.config.meteorSpeed + Math.random() * 2,
        length: this.config.meteorLength + Math.random() * 50,
        opacity: 0.6 + Math.random() * 0.4,
        life: 0,
        maxLife: 3.0 + Math.random() * 2.0,
      });
    }

    // 更新流星位置并移除超出生命周期的流星
    this.meteors = this.meteors.filter(meteor => {
      meteor.x += Math.cos(meteor.angle) * meteor.speed;
      meteor.y += Math.sin(meteor.angle) * meteor.speed;
      meteor.life += this.CONSTANTS.DELTA_TIME;

      const margin = 100;
      return meteor.life < meteor.maxLife
        && meteor.x > -margin
        && meteor.x < canvasWidth + margin
        && meteor.y > -margin
        && meteor.y < canvasHeight + margin;
    });
  }

  private getCachedColor(hex: string): { r: number; g: number; b: number; a: number } {
    if (!this.colorCache.has(hex)) {
      this.colorCache.set(hex, this.hexToRgb(hex));
    }

    return this.colorCache.get(hex)!;
  }

  private renderStars(): void {
    if (!this.starProgram) {
      return;
    }

    this.gl.useProgram(this.starProgram);

    // 准备星星数据并上传到GPU
    this.stars.forEach((star, i) => {
      this.arrays.star.positions[i * 2] = star.x;
      this.arrays.star.positions[i * 2 + 1] = star.y;
      this.arrays.star.sizes[i] = star.size;
      this.arrays.star.opacities[i] = star.opacity;
      this.arrays.star.shapes[i] = star.shape;

      const color = this.getCachedColor(star.color);
      this.arrays.star.colors[i * 3] = color.r / 255;
      this.arrays.star.colors[i * 3 + 1] = color.g / 255;
      this.arrays.star.colors[i * 3 + 2] = color.b / 255;
    });

    // 设置位置缓冲区
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.star.position);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, this.arrays.star.positions, this.gl.DYNAMIC_DRAW);
    this.gl.enableVertexAttribArray(this.locations.attributes.star.position);
    this.gl.vertexAttribPointer(this.locations.attributes.star.position, 2, this.gl.FLOAT, false, 0, 0);

    // 设置大小缓冲区
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.star.size);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, this.arrays.star.sizes, this.gl.DYNAMIC_DRAW);
    this.gl.enableVertexAttribArray(this.locations.attributes.star.size);
    this.gl.vertexAttribPointer(this.locations.attributes.star.size, 1, this.gl.FLOAT, false, 0, 0);

    // 设置透明度缓冲区
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.star.opacity);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, this.arrays.star.opacities, this.gl.DYNAMIC_DRAW);
    this.gl.enableVertexAttribArray(this.locations.attributes.star.opacity);
    this.gl.vertexAttribPointer(this.locations.attributes.star.opacity, 1, this.gl.FLOAT, false, 0, 0);

    // 设置颜色缓冲区
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.star.color);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, this.arrays.star.colors, this.gl.DYNAMIC_DRAW);
    this.gl.enableVertexAttribArray(this.locations.attributes.star.color);
    this.gl.vertexAttribPointer(this.locations.attributes.star.color, 3, this.gl.FLOAT, false, 0, 0);

    // 设置形状缓冲区
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.star.shape);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, this.arrays.star.shapes, this.gl.DYNAMIC_DRAW);
    this.gl.enableVertexAttribArray(this.locations.attributes.star.shape);
    this.gl.vertexAttribIPointer(this.locations.attributes.star.shape, 1, this.gl.INT, 0, 0);

    // 设置uniform变量
    this.gl.uniform2f(this.locations.uniforms.star.resolution, this.canvas.width, this.canvas.height);
    this.gl.uniform2f(this.locations.uniforms.star.mouse, this.mouseX, this.mouseY);
    this.gl.uniform1f(this.locations.uniforms.star.parallaxIntensity, this.config.parallaxIntensity);

    // 设置自定义纹理
    if (this.customTexture) {
      this.gl.activeTexture(this.gl.TEXTURE0);
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.customTexture);
      this.gl.uniform1i(this.locations.uniforms.star.customTexture, 0);
      this.gl.uniform1i(this.locations.uniforms.star.hasCustomTexture, 1);
    } else {
      this.gl.uniform1i(this.locations.uniforms.star.hasCustomTexture, 0);
    }

    // 绘制所有星星
    this.gl.drawArrays(this.gl.POINTS, 0, this.stars.length);
  }

  private renderMeteors(): void {
    if (!this.meteorProgram || this.meteors.length === 0) {
      return;
    }

    this.gl.useProgram(this.meteorProgram);

    // 从流星颜色数组中随机选择一个颜色
    const randomColorIndex = Math.floor(Math.random() * this.config.meteorColors.length);
    const meteorColor = this.getCachedColor(this.config.meteorColors[randomColorIndex]);
    const meteorColorRgb = [meteorColor.r / 255, meteorColor.g / 255, meteorColor.b / 255];
    const fadeStart = this.config.meteorFadeStart;
    const fadeDuration = 1 - fadeStart;

    this.meteors.forEach(meteor => {
      const segments = this.CONSTANTS.METEOR_SEGMENTS;
      const cosAngle = Math.cos(meteor.angle);
      const sinAngle = Math.sin(meteor.angle);

      // 生成流星线段的顶点数据
      for (let i = 0; i < segments; i++) {
        const t = i / (segments - 1);
        this.arrays.meteor.positions[i * 2] = meteor.x + cosAngle * meteor.length * t;
        this.arrays.meteor.positions[i * 2 + 1] = meteor.y + sinAngle * meteor.length * t;
        this.arrays.meteor.progress[i] = t;
      }

      // 设置位置缓冲区
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.meteor.position);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, this.arrays.meteor.positions, this.gl.DYNAMIC_DRAW);
      this.gl.enableVertexAttribArray(this.locations.attributes.meteor.position);
      this.gl.vertexAttribPointer(this.locations.attributes.meteor.position, 2, this.gl.FLOAT, false, 0, 0);

      // 设置进度缓冲区（用于渐变效果）
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.meteor.progress);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, this.arrays.meteor.progress, this.gl.DYNAMIC_DRAW);
      this.gl.enableVertexAttribArray(this.locations.attributes.meteor.progress);
      this.gl.vertexAttribPointer(this.locations.attributes.meteor.progress, 1, this.gl.FLOAT, false, 0, 0);

      // 设置uniform变量
      this.gl.uniform2f(this.locations.uniforms.meteor.resolution, this.canvas.width, this.canvas.height);

      // 计算生命周期相关的透明度
      const lifeProgress = meteor.life / meteor.maxLife;
      const fadeProgress = Math.max(0, Math.min(1, (lifeProgress - fadeStart) / fadeDuration));
      const baseOpacity = meteor.opacity * (1 - fadeProgress);
      this.gl.uniform1f(this.locations.uniforms.meteor.opacity, baseOpacity);

      // 设置流星颜色
      this.gl.uniform3f(this.locations.uniforms.meteor.color, meteorColorRgb[0], meteorColorRgb[1], meteorColorRgb[2]);

      // 绘制流星线段
      this.gl.lineWidth(3.0);
      this.gl.drawArrays(this.gl.LINE_STRIP, 0, segments);
    });
  }

  private render(): void {
    this.time += this.CONSTANTS.DELTA_TIME;

    // 平滑鼠标移动
    this.mouseX += (this.targetMouseX - this.mouseX);
    this.mouseY += (this.targetMouseY - this.mouseY);

    // 设置背景色（支持透明背景）
    if (this.config.backgroundColor === 'transparent') {
      this.gl.clearColor(0, 0, 0, 0);
    }
    else {
      const color = this.getCachedColor(this.config.backgroundColor);
      this.gl.clearColor(color.r / 255, color.g / 255, color.b / 255, color.a);
    }

    this.gl.clear(this.gl.COLOR_BUFFER_BIT);

    // 启用Alpha混合
    this.gl.enable(this.gl.BLEND);
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);

    // 更新和渲染所有元素
    this.updateStars();
    this.updateMeteors();
    this.renderStars();
    this.renderMeteors();

    // 继续动画循环
    this.animationId = requestAnimationFrame(() => this.render());
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number; a: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: 1,
      }
      : { r: 0, g: 0, b: 0, a: 1 };
  }

  public start(): void {
    if (!this.animationId) {
      this.render();
    }
  }

  public stop(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  public setEventElement(element: HTMLElement): void {
    // 移除现有事件监听器
    this.removeEventListeners();

    this.eventElement = element;
    this.setupEventListeners();
  }

  private removeEventListeners(): void {
    if (this.eventElement) {
      this.eventElement.removeEventListener('mousemove', this.mouseMoveHandler);
      this.eventElement.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }
    if (this.canvas !== this.eventElement) {
      this.canvas.removeEventListener('mousemove', this.mouseMoveHandler);
      this.canvas.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }
  }

  private mouseMoveHandler = (e: MouseEvent) => {
    const rect = this.canvas.getBoundingClientRect();
    this.targetMouseX = (e.clientX - rect.left) / rect.width - 0.5;
    this.targetMouseY = (e.clientY - rect.top) / rect.height - 0.5;
  };

  private mouseLeaveHandler = () => {
  };

  public updateConfig(newConfig: Partial<StarfieldConfig>): void {
    this.config = { ...this.config, ...newConfig };

    if (newConfig.starCount !== undefined) {
      this.arrays.star.positions = new Float32Array(this.config.starCount * 2);
      this.arrays.star.sizes = new Float32Array(this.config.starCount);
      this.arrays.star.opacities = new Float32Array(this.config.starCount);
      this.arrays.star.colors = new Float32Array(this.config.starCount * 3);
      this.arrays.star.shapes = new Int32Array(this.config.starCount);
      this.generateStars();
    }

    if (newConfig.starShapes !== undefined) {
      this.generateStars();
    }

    if (newConfig.starColors !== undefined || newConfig.meteorColors !== undefined || newConfig.backgroundColor !== undefined) {
      this.colorCache.clear();
      this.preCacheColors();
    }

    if (newConfig.customStarTexture !== undefined) {
      if (this.customTexture) {
        this.gl.deleteTexture(this.customTexture);
        this.customTexture = null;
      }
      this.loadCustomTexture();
    }
  }

  public destroy(): void {
    this.stop();
    this.removeEventListeners();

    // 释放WebGL资源
    if (this.customTexture) {
      this.gl.deleteTexture(this.customTexture);
    }
    if (this.starProgram) {
      this.gl.deleteProgram(this.starProgram);
    }
    if (this.meteorProgram) {
      this.gl.deleteProgram(this.meteorProgram);
    }
    if (this.buffers.star.position) {
      this.gl.deleteBuffer(this.buffers.star.position);
    }
    if (this.buffers.star.size) {
      this.gl.deleteBuffer(this.buffers.star.size);
    }
    if (this.buffers.star.opacity) {
      this.gl.deleteBuffer(this.buffers.star.opacity);
    }
    if (this.buffers.star.color) {
      this.gl.deleteBuffer(this.buffers.star.color);
    }
    if (this.buffers.star.shape) {
      this.gl.deleteBuffer(this.buffers.star.shape);
    }
    if (this.buffers.meteor.position) {
      this.gl.deleteBuffer(this.buffers.meteor.position);
    }
    if (this.buffers.meteor.progress) {
      this.gl.deleteBuffer(this.buffers.meteor.progress);
    }
  }
}
